import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import React, { Fragment, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { useUser } from "../../user/utils/user";

import dataHeader from "../../data/header.js";
import delineation2Header from "../../delineation2/header.js";
import delineationHeader from "../../delineation/header.js";
import landcover2Header from "../../landcover2/header.js";
import landcover3Header from "../../landcover3/header.js";
import landcover4Header from "../../landcover4/header.js";
import landcoverHeader from "../../landcover/header.js";
import userHeader from "../../user/header.js";
import { useRoleClientSelection } from "../contexts/RoleClientSelectionContext";
import logo from "../logo.svg";
import { resolveRoute } from "../utils/routeHelpers";
import ImpersonationModal from "./ImpersonationModal";

function HeaderLink({
  header,
  selectedRole,
  selectedClient,
  children,
  onClick,
}) {
  const props = {
    onClick,
    className:
      "-m-3 block rounded-md p-3 transition duration-150 ease-in-out hover:bg-gray-50",
  };

  const resolvedTo =
    header.external || header.to.startsWith("/users")
      ? header.to
      : resolveRoute(header.to, {
          role: selectedRole,
          client: selectedClient,
        });

  if (header.external) {
    return (
      <a href={resolvedTo} {...props} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  } else {
    return (
      <Link to={resolvedTo} {...props}>
        {children}
      </Link>
    );
  }
}

function Header({ t }) {
  const { user, logout } = useUser();
  const { selectedRole, selectedClient } = useRoleClientSelection();
  const navigate = useNavigate();
  const buttonRef = useRef();
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const openImpersonateModal = () => setShowImpersonateModal(true);

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  const prefixHeaders = (makeHeaders, addSpacer = true) => {
    const headers = makeHeaders(user, t);
    if (headers.length > 0 && addSpacer) {
      headers.push(null);
    }
    return headers;
  };

  const roleHeaderMap = {
    data: dataHeader,
    delineation: delineationHeader,
    delineation2: delineation2Header,
    landcover: landcoverHeader,
    landcover2: landcover2Header,
    landcover3: landcover3Header,
    landcover4: landcover4Header,
  };

  // Only show headers for the selected role, plus user headers
  const normalizedRole = selectedRole
    ? String(selectedRole).toLowerCase()
    : null;
  const headers = normalizedRole
    ? [
        ...(roleHeaderMap[normalizedRole]
          ? prefixHeaders(roleHeaderMap[normalizedRole])
          : []),
        ...prefixHeaders(userHeader, false),
      ]
    : [...prefixHeaders(userHeader, false)];

  return (
    <header className="bg-cyan-700 shadow">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" aria-label="Top">
        <div className="w-full items-center justify-between py-3 sm:flex">
          <div className="flex items-center">
            <Link
              to="/"
              className="text-white text-xl sm:text-2xl font-base flex"
            >
              <div className="h-10 w-10 pt-1 pl-0.5 bg-cyan-500 rounded-lg">
                <img className="h-full" src={logo} alt="" />
              </div>
              <span className="ml-4 leading-10 align-middle">
                {t(
                  "common.groundTruthDataCollection",
                  "Ground Truth Data Collection",
                )}
              </span>
            </Link>
          </div>
          <div className="mt-4 sm:mt-0 flex flex-row-reverse">
            {user ? (
              <React.Fragment>
                <Popover className="relative">
                  {({ open }) => (
                    <>
                      <Popover.Button
                        ref={buttonRef}
                        as="div"
                        className={classNames(
                          open ? "text-gray-200" : "text-gray-100",
                          "group inline-flex py-2 text-sm font-medium text-white hover:text-gray-200",
                        )}
                      >
                        <span>{t("user.account", "Account")}</span>
                        <ChevronDownIcon
                          className={classNames(
                            open ? "text-gray-200" : "text-gray-100",
                            "ml-2 h-5 w-5 group-hover:text-gray-200",
                          )}
                          aria-hidden="true"
                        />
                      </Popover.Button>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Popover.Panel className="absolute right-0 px-3 z-10 mt-2 w-screen max-w-xs transform px-2 sm:px-0">
                          <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                            <div className="relative grid gap-6 bg-white px-5 py-4 sm:gap-6 sm:p-8">
                              {selectedRole && selectedClient && (
                                <>
                                  <div className="-m-3 p-3">
                                    <p className="text-base font-medium text-gray-900 capitalize">
                                      {selectedRole} / {selectedClient}
                                    </p>
                                    {user.user?.role_clients &&
                                      user.user.role_clients.length > 1 && (
                                        <Link
                                          to="/role-selection"
                                          onClick={() => {
                                            buttonRef.current?.click();
                                          }}
                                          className="mt-1 text-sm text-cyan-600 hover:text-cyan-800"
                                        >
                                          {t("common.switch", "Switch")}
                                        </Link>
                                      )}
                                  </div>
                                  <div className="relative" aria-hidden="true">
                                    <div className="absolute inset-0 flex items-center">
                                      <div className="w-full border-t border-gray-300" />
                                    </div>
                                  </div>
                                </>
                              )}
                              {!selectedRole &&
                                user.user?.role_clients &&
                                user.user.role_clients.length > 1 && (
                                  <>
                                    <Link
                                      to="/role-selection"
                                      onClick={() => {
                                        buttonRef.current?.click();
                                      }}
                                      className="-m-3 block rounded-md p-3 transition duration-150 ease-in-out hover:bg-gray-50"
                                    >
                                      <p className="text-base font-medium text-gray-900">
                                        {t(
                                          "common.selectRoleClient",
                                          "Select Role/Client",
                                        )}
                                      </p>
                                    </Link>
                                    <div
                                      className="relative"
                                      aria-hidden="true"
                                    >
                                      <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-300" />
                                      </div>
                                    </div>
                                  </>
                                )}
                              {headers.map((h, i) => {
                                if (h) {
                                  if (h.action === "impersonate") {
                                    return (
                                      <button
                                        key={i}
                                        onClick={() => {
                                          openImpersonateModal();
                                          buttonRef.current?.click();
                                        }}
                                        className="-m-3 block w-full rounded-md p-3 text-left transition duration-150 ease-in-out hover:bg-gray-50"
                                      >
                                        <p className="text-base font-medium text-gray-900">
                                          {h.title}
                                        </p>
                                        <p className="mt-1 text-sm text-gray-500">
                                          {h.desc}
                                        </p>
                                      </button>
                                    );
                                  }
                                  return (
                                    <HeaderLink
                                      header={h}
                                      selectedRole={selectedRole}
                                      selectedClient={selectedClient}
                                      key={h.to}
                                      onClick={() => {
                                        buttonRef.current?.click();
                                      }}
                                    >
                                      <p className="text-base font-medium text-gray-900">
                                        {h.title}
                                      </p>
                                      <p className="mt-1 text-sm text-gray-500">
                                        {h.desc}
                                      </p>
                                    </HeaderLink>
                                  );
                                } else {
                                  return (
                                    <div className="relative" key={i}>
                                      <div
                                        className="absolute inset-0 flex items-center"
                                        aria-hidden="true"
                                      >
                                        <div className="w-full border-t border-gray-300" />
                                      </div>
                                    </div>
                                  );
                                }
                              })}
                              <button
                                type="submit"
                                onClick={() => {
                                  logout();
                                  open = false;
                                  navigate("/");
                                }}
                                className="inline-block rounded-md border border-transparent bg-white py-2 px-4 text-sm font-medium text-cyan-600 hover:bg-gray-100"
                              >
                                {t("user.logOut", "Log out")}
                              </button>
                            </div>
                          </div>
                        </Popover.Panel>
                      </Transition>
                    </>
                  )}
                </Popover>
              </React.Fragment>
            ) : (
              <React.Fragment>
                {/* <Link
                  to="/users/signup"
                  className="ml-4 inline-block rounded-md border border-transparent bg-white py-2 px-4 text-sm font-medium text-cyan-600 hover:bg-gray-100"
                >
                  {t("user.signUp", "Sign up")}
                </Link> */}
                <Link
                  to="/users/login"
                  className="inline-block rounded-md border border-transparent bg-cyan-500 py-2 px-4 text-sm font-medium text-white hover:bg-opacity-75"
                >
                  {t("user.signIn", "Sign in")}
                </Link>
              </React.Fragment>
            )}
          </div>
        </div>
      </nav>
      <ImpersonationModal
        isOpen={showImpersonateModal}
        onClose={() => setShowImpersonateModal(false)}
      />
    </header>
  );
}

export default withTranslation()(Header);
